import type { DataOption } from '@/common.types'
import type {
	OptimizationGroupsType,
	SelectedOptimizationGroupType,
} from '../lib/helpers'

import React, { useMemo, useState } from 'react'
import { isEqual, map, prop, sortedUniqBy } from 'lodash/fp'
import { ChevronDownIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/shared/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'

type OptimizationGroupsDropdownProps = {
	selectedOgs: SelectedOptimizationGroupType[] | null
	handleClear: () => void
	handleApply: (value: SelectedOptimizationGroupType[]) => void
	isDisabled: boolean
	optimizationGroups: OptimizationGroupsType[]
}

export const OptimizationGroupsDropdown = (
	props: OptimizationGroupsDropdownProps
) => {
	const { t } = useTranslation()

	const [open, setOpen] = useState(false)
	const options = useMemo(() => {
		if (props.optimizationGroups) {
			return props.optimizationGroups.map(({ id, name }) => ({
				value: id,
				label: name,
			}))
		}

		return []
	}, [props.optimizationGroups])

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button>
					{t('opt.summary.select_og', { defaultValue: 'Select OG' })}{' '}
					{`(${props.selectedOgs?.length || 0})`}
					<ChevronDownIcon className='opacity-50' />
				</Button>
			</PopoverTrigger>
			<PopoverContent align='end' side='bottom' className='w-auto p-0'>
				<Content
					{...props}
					handleClose={() => setOpen(false)}
					options={options}
					optimizationGropus={props.optimizationGroups}
				/>
			</PopoverContent>
		</Popover>
	)
}

export const Content = ({
	selectedOgs,
	handleClose,
	handleApply,
	options,
	optimizationGropus,
}: {
	selectedOgs: SelectedOptimizationGroupType[] | null
	handleClose: () => void
	handleApply: (value: SelectedOptimizationGroupType[]) => void
	options: DataOption[]
	optimizationGropus: OptimizationGroupsType[]
}) => {
	const { t } = useTranslation()

	const [selected, setSelected] = useState<
		SelectedOptimizationGroupType[] | null
	>(selectedOgs)
	const initialSelectedOgs = useMemo(() => selectedOgs, [])
	const values = selected?.map(({ id }) => id) || []
	const isAllSelected = selected?.length === optimizationGropus.length
	const handleChange = (optimizationGroups: DataOption[]) => {
		const selected = optimizationGroups.map(({ value, label }) => ({
			name: label,
			id: value,
		}))
		setSelected(selected)
	}
	const isButtonsDisabled =
		isEqual(
			map(prop('id'), sortedUniqBy(prop('id'), selected)),
			map(prop('id'), sortedUniqBy(prop('id'), initialSelectedOgs))
		) || !selected?.length

	return (
		<div className='w-80 text-sm'>
			<Command>
				<CommandInput
					placeholder={t('general_search')}
					autoFocus
					className='h-9'
				/>
				<CommandList>
					<CommandEmpty>
						{t('general_no_results_found', {
							defaultValue: 'No results found',
						})}
					</CommandEmpty>
					<CommandGroup>
						<CommandItem
							onSelect={() => {
								handleChange(isAllSelected ? [] : options)
							}}
						>
							<Checkbox
								checked={isAllSelected}
								indeterminate={!isAllSelected && !!selected?.length}
							/>
							{t('general_select_all', { defaultValue: 'Select all' })}
						</CommandItem>
					</CommandGroup>
					<CommandSeparator />
					<CommandGroup>
						{options.map(({ value, label }) => (
							<CommandItem
								key={value}
								value={value}
								onSelect={(currentValue) => {
									const newValues = values.some(
										(value) => value === currentValue
									)
										? values.filter((value) => value !== currentValue)
										: [...values, currentValue]
									handleChange(
										options.filter(({ value }) => newValues.includes(value))
									)
								}}
							>
								<Checkbox checked={values.includes(value)} />
								{label}
							</CommandItem>
						))}
					</CommandGroup>
				</CommandList>
			</Command>
			<div className='flex justify-end gap-2 border-t px-4 py-2'>
				<Button size='sm' onClick={handleClose}>
					{t('general_cancel')}
				</Button>
				<Button
					disabled={isButtonsDisabled}
					variant='primary'
					size='sm'
					onClick={() => {
						handleApply(selected!)
						handleClose()
					}}
				>
					{t('general_apply')}
				</Button>
			</div>
		</div>
	)
}
